import { X } from 'lucide-react'
import { useState } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useCreateDevice,
  useUpdateDevice,
  type Device,
  type DeviceType,
  type CreateDeviceInput,
} from '@/hooks'

interface DeviceFormProps {
  device?: Device
  onClose: () => void
  onSuccess: () => void
}

const deviceTypes: { value: DeviceType; label: string }[] = [
  { value: 'computer', label: '电脑' },
  { value: 'monitor', label: '显示器' },
  { value: 'keyboard', label: '键盘' },
  { value: 'mouse', label: '鼠标' },
  { value: 'other', label: '其他' },
]

export function DeviceForm({ device, onClose, onSuccess }: DeviceFormProps) {
  const isEditing = !!device
  const [formData, setFormData] = useState<CreateDeviceInput>({
    name: device?.name ?? '',
    type: device?.type ?? 'computer',
    brand: device?.brand ?? '',
    model: device?.model ?? '',
    serialNumber: device?.serialNumber ?? '',
    purchaseDate: device?.purchaseDate ?? '',
    purchasePrice: device?.purchasePrice ?? undefined,
    notes: device?.notes ?? '',
  })
  const [error, setError] = useState<string | null>(null)

  const createMutation = useCreateDevice()
  const updateMutation = useUpdateDevice()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const submitAsync = async () => {
      try {
        const data = {
          ...formData,
          brand: formData.brand || undefined,
          model: formData.model || undefined,
          purchaseDate: formData.purchaseDate || undefined,
          notes: formData.notes || undefined,
        }

        if (isEditing && device) {
          await updateMutation.mutateAsync({
            id: device.id,
            data,
          })
        } else {
          await createMutation.mutateAsync(data)
        }
        onSuccess()
      } catch (err) {
        setError(err instanceof Error ? err.message : '操作失败')
      }
    }

    void submitAsync()
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg bg-background p-6 shadow-lg max-h-[90vh] overflow-y-auto">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{isEditing ? '编辑设备' : '新建设备'}</h2>
          <button onClick={onClose} className="rounded p-1 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">设备名称 *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="请输入设备名称"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">设备类型 *</label>
            <Select
              value={formData.type}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, type: value as DeviceType }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="选择设备类型" />
              </SelectTrigger>
              <SelectContent>
                {deviceTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium">品牌</label>
              <input
                type="text"
                value={formData.brand ?? ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, brand: e.target.value }))}
                className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="如 Apple, Dell"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">型号</label>
              <input
                type="text"
                value={formData.model ?? ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, model: e.target.value }))}
                className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="如 MacBook Pro 14"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">序列号 *</label>
            <input
              type="text"
              value={formData.serialNumber}
              onChange={(e) => setFormData((prev) => ({ ...prev, serialNumber: e.target.value }))}
              className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="请输入设备序列号"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium">购买日期</label>
              <input
                type="date"
                value={formData.purchaseDate ?? ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, purchaseDate: e.target.value }))}
                className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">购买价格 (分)</label>
              <input
                type="number"
                value={formData.purchasePrice ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    purchasePrice: e.target.value ? parseInt(e.target.value, 10) : undefined,
                  }))
                }
                className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="0"
                min="0"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">备注</label>
            <textarea
              value={formData.notes ?? ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
              className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="请输入备注信息"
              rows={3}
            />
          </div>

          {error && <div className="text-sm text-destructive">{error}</div>}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isPending ? '保存中...' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
